/**
 * Pure helpers for offline sync ordering.
 * Kept separate from syncQueue.js so node:test can import without Dexie/Vue.
 */

/**
 * Normalize a job id for queue dependency matching (strip internal_ prefix).
 */
export function resolveQueueItemJobId(item) {
  if (!item) return null
  const raw = item.jobId ?? item.data?.job_id ?? item.data?.jobId ?? null
  if (raw == null || raw === '') return null
  return String(raw).replace(/^internal_/, '')
}

function isPaymentRecordUrl(url) {
  const u = String(url || '')
  return u.includes('record-payment') || u.includes('record-for-job')
}

/**
 * Payment and job-completion must wait for prior invoice mutations / media
 * uploads for the same job — otherwise reconnect replays "done" before the
 * deletes/photos that the server completion gate expects.
 */
export function isCompletionSensitiveQueueItem(item) {
  if (!item) return false
  if (item.type === 'tech_done' || item.type === 'tech_complete_part') return true
  if (item.type === 'invoice_action' && isPaymentRecordUrl(item.url)) return true
  return false
}

export function isJobPrerequisiteQueueItem(item) {
  if (!item) return false
  if (item.type === 'upload_photo') return true
  if (item.type === 'invoice_action' && !isPaymentRecordUrl(item.url)) return true
  return false
}

/**
 * True when `item` should be skipped this sync pass because same-job
 * prerequisites are still pending (and not yet completed in this pass).
 */
export function shouldDeferQueueItem(item, siblings, completedIds = new Set()) {
  if (!isCompletionSensitiveQueueItem(item)) return false
  const jobId = resolveQueueItemJobId(item)
  if (!jobId) return false
  return (siblings || []).some((other) => {
    if (!other || other.id === item.id || completedIds.has(other.id)) return false
    if (!isJobPrerequisiteQueueItem(other)) return false
    const otherJob = resolveQueueItemJobId(other)
    return otherJob && otherJob === jobId
  })
}
